import { chatWsUrl } from './config'

/**
 * 群聊WebSocket管理类
 * 单例模式，全局共用一个连接
 */
class ChatSocketManager {
  constructor() {
    this.ws = null
    this.userId = null
    this.connected = false
    this.connecting = false // 正在连接中，防止重复创建
    this.reconnectTimer = null
    this.heartbeatTimer = null
    this.listeners = {} // 事件监听 { type: [callback] }
    this.manualClose = false
  }

  /**
   * 连接WebSocket（幂等：已连接或连接中则跳过）
   */
  connect(userId) {
    if (!userId) {
      console.error('chatSocket: userId为空，无法连接')
      return
    }

    // 已经连接成功，且是同一个用户，直接返回
    if (this.connected && this.userId === userId) {
      console.log('chatSocket: 已连接，跳过')
      return
    }

    // 正在连接中，且是同一个用户，跳过
    if (this.connecting && this.userId === userId) {
      console.log('chatSocket: 正在连接中，跳过')
      return
    }

    // 不同用户或异常状态，先关闭旧连接
    if (this.ws) {
      this.close()
    }

    this.userId = userId
    this.manualClose = false
    this.connecting = true

    const url = `${chatWsUrl}?userId=${userId}`
    console.log('chatSocket: 正在连接', url)

    let ws
    try {
      ws = new WebSocket(url)
      this.ws = ws
    } catch (e) {
      console.error('chatSocket: 创建连接失败', e)
      this.connecting = false
      this.scheduleReconnect()
      return
    }

    ws.onopen = () => {
      console.log('chatSocket: 连接成功')
      this.connecting = false
      this.connected = true
      this.startHeartbeat()
      this.emit('connected', {})
    }

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data)
        this.emit(msg.type, msg.data)
      } catch (e) {
        console.error('chatSocket: 消息解析失败', e)
      }
    }

    ws.onclose = (event) => {
      console.log('chatSocket: 连接关闭', event.code, event.reason)
      this.connecting = false
      this.connected = false
      this.stopHeartbeat()
      this.emit('disconnected', { code: event.code, reason: event.reason })

      // 被服务端主动断开（如被禁用），不自动重连
      if (event.code === 1008) {
        this.emit('banned', { reason: event.reason })
        return
      }

      if (!this.manualClose) {
        this.scheduleReconnect()
      }
    }

    ws.onerror = (error) => {
      console.error('chatSocket: 连接错误', error)
      this.connecting = false
      this.emit('error', error)
    }
  }

  /**
   * 关闭连接
   */
  close() {
    this.manualClose = true
    this.stopHeartbeat()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      try {
        // 只有OPEN或CONNECTING状态才调用close
        if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close()
        }
      } catch (e) { }
      this.ws = null
    }
    this.connected = false
    this.connecting = false
  }

  /**
   * 发送消息
   */
  send(type, data = {}) {
    if (!this.connected || !this.ws) {
      console.error('chatSocket: 未连接，无法发送')
      return false
    }
    try {
      this.ws.send(JSON.stringify({ type, data, time: new Date().toISOString() }))
      return true
    } catch (e) {
      console.error('chatSocket: 发送失败', e)
      return false
    }
  }

  /**
   * 心跳
   */
  startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      if (this.connected) {
        this.send('ping', {})
      }
    }, 25000)
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  /**
   * 定时重连
   */
  scheduleReconnect() {
    if (this.reconnectTimer || this.manualClose) return
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (this.userId) {
        this.connect(this.userId)
      }
    }, 3000)
  }

  /**
   * 事件监听
   */
  on(type, callback) {
    if (!this.listeners[type]) {
      this.listeners[type] = []
    }
    this.listeners[type].push(callback)
  }

  off(type, callback) {
    if (!this.listeners[type]) return
    this.listeners[type] = this.listeners[type].filter(cb => cb !== callback)
  }

  emit(type, data) {
    if (!this.listeners[type]) return
    this.listeners[type].forEach(cb => {
      try { cb(data) } catch (e) { console.error('chatSocket listener error:', e) }
    })
  }
}

// 导出单例
const chatSocket = new ChatSocketManager()
export default chatSocket
